import {ContextProvider} from '@lit-labs/context';
import {html} from 'lit';
import type {Constructor} from 'lit-element';
import {customElement, property} from 'lit-element';
import {state} from 'lit/decorators.js';

import {ImmutableComponent} from '#lit/components/mixins/immutable_component/index.js';
import {SessionContext} from '#lit/contexts/session.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import type {FeatureFlagKey} from '#lit/lib/feature_flags/FeatureFlag.js';
import type {ApplicationFeatureFlags} from '#lit/types/feature_flags.js';
import type {Nullable} from '#lit/types/general.js';
import type {Organization, Team} from '#lit/types/teams.js';
import type {User} from '#lit/types/user.js';

import type {Model} from './architecture.js';
import {main, Msg} from './architecture.js';
import styles from './styles.scss';

export const SESSION_PROVIDER_TAG = 'session-provider';

@customElement(SESSION_PROVIDER_TAG)
export class SessionProvider extends ImmutableComponent<
  Constructor<FlywheelComponent>,
  Model,
  Msg
>({component: FlywheelComponent, main}) {
  static get styles() {
    return [styles];
  }

  @property({type: Object})
  get user(): Nullable<User> {
    return this.model.user;
  }
  set user(value) {
    this.handleUpdate(Msg.setUser(value));
  }

  @property({type: Object})
  get organization(): Nullable<Organization> {
    return this.model.organization;
  }
  set organization(value) {
    this.handleUpdate(Msg.setOrg(value));
  }

  @property({type: Object})
  get team(): Nullable<Team> {
    return this.model.team;
  }
  set team(value) {
    this.handleUpdate(Msg.setTeam(value));
  }

  @property({type: String})
  mixpanelProjectId = '';

  @property({type: String})
  mixpanelDistinctId = '';

  @property({type: String})
  gcpImpersonationServiceAccount = '';

  @property({type: String})
  embed = 'false';

  @property({type: Object})
  flags: ApplicationFeatureFlags = {};

  @state()
  session = new ContextProvider(
    this,
    SessionContext,
    Object.freeze({
      user: () => this.model.user,
      embed: () => this.model.embed,
      enabled: (...keys: FeatureFlagKey[]) => {
        return keys.every(key => this.model.flags.enabled(key));
      },
    })
  );

  render() {
    return html`<slot></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [SESSION_PROVIDER_TAG]: SessionProvider;
  }
}
